<template>
  <v-card class="pa-4 ncrm-activity-timeline">
    <div class="d-flex align-center justify-space-between ga-2">
      <div>
        <div class="text-subtitle-1 ncrm-activity-timeline__title">
          {{ titleText }}
        </div>
        <div v-if="subtitleText" class="text-body-2 text-medium-emphasis">
          {{ subtitleText }}
        </div>
      </div>
      <div class="d-flex align-center ga-2">
        <v-btn
          v-if="allowCreate"
          color="primary"
          variant="elevated"
          size="small"
          :disabled="!canLoad"
          @click="openComposer"
        >
          <v-icon start>mdi-plus</v-icon>
          {{ $t("ncrm.activity.actions.add") }}
        </v-btn>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-3" />

    <div v-if="!loading" class="mt-3">
      <v-alert
        v-if="!canLoad"
        type="info"
        density="comfortable"
      >
        {{ $t("ncrm.activity.invalidEntity") }}
      </v-alert>

      <v-alert
        v-else-if="!displayActivities.length"
        type="info"
        density="comfortable"
      >
        {{ $t("ncrm.activity.empty") }}
      </v-alert>

      <div v-else class="ncrm-activity-timeline__rail">
        <span class="ncrm-activity-timeline__rail-line" aria-hidden="true"></span>
        <NcrmActivityCard
          v-for="(activity, index) in displayActivities"
          :key="`activity-${activity.id || activity.created_at || index}`"
          v-bind="buildActivityView(activity)"
          :allow-create="allowCreate"
          :pending="pendingCloseMap[activity.id] || null"
          :action-loading="actionLoadingId === activity.id"
          :busy="submitting || deleteLoading"
          :recording-loading="downloadingRecordingId === activity.id"
          @close="requestCloseActivity(activity)"
          @undo="undoPendingClose(activity.id)"
          @edit="openComposerForEdit(activity)"
          @delete="openDeleteDialog(activity)"
          @open-origin="openOriginContext(activity)"
          @download-recording="downloadRecording(activity)"
        />
      </div>
    </div>

    <teleport v-if="allowCreate" to="body">
      <NcrmRecordEditDialog
        :model-value="resolvedComposerOpen"
        :title="composerTitle"
        :subtitle="$t('ncrm.activity.subtitle')"
        :loading="submitting"
        :save-disabled="submitDisabled"
        :max-width="620"
        :extra-action-label="$t('app.actions.clear')"
        :extra-action-disabled="submitting"
        @update:model-value="updateComposerOpen"
        @extra-action="resetForm"
        @save="saveComposer"
      >
        <v-row dense>
          <v-col v-if="composerNotice.message" cols="12">
            <v-alert :type="composerNotice.color" density="comfortable" class="mb-1">
              {{ composerNotice.message }}
            </v-alert>
          </v-col>
          <v-col cols="12" :md="showStatusField ? 6 : 12">
            <v-select
              v-model="form.activity_type"
              :items="activityTypeItems"
              item-title="label"
              item-value="value"
              :menu-props="selectMenuProps"
              :label="$t('ncrm.activity.fields.type')"
              :disabled="activityTypeFieldDisabled"
            />
          </v-col>
          <v-col v-if="showStatusField" cols="12" md="6">
            <v-select
              v-model="form.status"
              :items="activityStatusItems"
              item-title="label"
              item-value="value"
              :menu-props="selectMenuProps"
              :label="$t('ncrm.activity.fields.status')"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.title"
              :label="$t('ncrm.activity.fields.title')"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="form.content"
              :label="$t('ncrm.activity.fields.content')"
              rows="4"
              auto-grow
            />
          </v-col>
          <v-col cols="12">
            <NcrmActivityAssigneeField
              v-if="resolvedComposerOpen"
              v-model="form.owner_user_id"
              :disabled="submitting"
              :menu-props="selectMenuProps"
            />
          </v-col>
          <v-col v-if="requiresDueAt" cols="12" md="8">
            <NcrmDateTimeField v-model="form.due_at" :label="$t('ncrm.activity.fields.dueAtRequired')" />
          </v-col>
          <v-col v-if="showPriorityField" :cols="12" :md="requiresDueAt ? 4 : 12">
            <v-select
              v-model="form.priority"
              :items="activityPriorityItems"
              item-title="label"
              item-value="value"
              :menu-props="selectMenuProps"
              :label="$t('ncrm.activity.fields.priority')"
            />
          </v-col>
          <v-col v-if="requiresDueAt" cols="12">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.activity.validation.dueRequired") }}
            </div>
          </v-col>
        </v-row>
      </NcrmRecordEditDialog>
    </teleport>

    <v-dialog v-model="deleteDialog" max-width="460">
      <v-card>
        <v-card-title class="text-h6 py-4">
          {{ $t("ncrm.activity.dialogs.deleteTitle") }}
        </v-card-title>
        <v-card-text class="pt-0">
          <div class="text-body-2 text-medium-emphasis mb-3">
            {{ $t("ncrm.activity.dialogs.deleteHint") }}
          </div>
          <v-text-field
            v-model="deleteConfirmText"
            :label="$t('ncrm.activity.dialogs.deleteTokenLabel')"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="text" :disabled="deleteLoading" @click="closeDeleteDialog">
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleteLoading"
            :disabled="deleteSubmitDisabled"
            @click="confirmDelete"
          >
            {{ $t("ncrm.activity.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="3200" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </v-card>
</template>

<script>
import axios from "axios";
import NcrmActivityCard from "@/components/ncrm/shared/NcrmActivityCard.vue";
import NcrmActivityAssigneeField from "@/components/ncrm/shared/NcrmActivityAssigneeField.vue";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  getNcrmActivityPriorityOptions,
  getNcrmActivityStatusOptions,
  getNcrmActivityTypeOptions,
  isNcrmActivityOpenStatus,
  isNcrmActivityProgrammableType,
  normalizeNcrmActivityDateTimeInput,
  normalizeNcrmActivityEntityType,
  normalizeNcrmActivityOriginEntityType,
  normalizeNcrmActivityItem,
  normalizeNcrmActivityList,
  normalizeNcrmActivityPriority,
  normalizeNcrmActivityStatus,
  normalizeNcrmActivityType,
  parseNcrmActivityEntityId,
  resolveNcrmActivityDefaultClosedStatus,
  resolveNcrmActivityDefaultStatus,
} from "@/Utile/ncrmActivity.mjs";
import { sortNcrmActivityTimelineItems } from "@/Utile/ncrmActivityTimeline.mjs";
import { ncrmActivityTimelineActionMethods } from "@/Utile/ncrmActivityTimelineActions.mjs";
import { ncrmActivityTimelineRecordingMethods } from "@/Utile/ncrmActivityTimelineRecording.mjs";
import { ncrmActivityTimelineScopeComputed, ncrmActivityTimelineScopeMethods } from "@/Utile/ncrmActivityTimelineScope.mjs";
import {
  buildDefaultNcrmActivityTimelineForm,
  buildNcrmActivityTimelineFormPayload,
  hydrateNcrmActivityTimelineForm,
} from "@/Utile/ncrmActivityTimelineForm.mjs";

const DELETE_CONFIRM_TOKEN = "DELETE";
const PENDING_CLOSE_SECONDS = 3;
const ACTIVITY_ENTITY_CONTEXT_OPTIONS = new Set(["deal", "onboarding", "lead"]);
const PRIORITY_SORT_FETCH_LIMIT = 200;
const PRIORITY_SORT_MAX_PAGES = 50;

const normalizeEntityContext = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return "";
  return ACTIVITY_ENTITY_CONTEXT_OPTIONS.has(normalized) ? normalized : "";
};

export default {
  name: "NcrmActivityTimeline",
  components: {
    NcrmActivityCard,
    NcrmActivityAssigneeField,
    NcrmDateTimeField,
    NcrmRecordEditDialog,
  },
  props: {
    entityType: {
      type: String,
      default: "",
    },
    entityId: {
      type: [String, Number],
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    maxItems: {
      type: Number,
      default: 20,
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
    composerOpen: {
      type: Boolean,
      default: false,
    },
    originEntityType: {
      type: String,
      default: "",
    },
    originEntityId: {
      type: [String, Number],
      default: "",
    },
    entityContext: {
      type: String,
      default: "",
    },
    onboardingId: {
      type: [String, Number],
      default: "",
    },
    includeOriginLeadActivity: {
      type: Boolean,
      default: false,
    },
    sortMode: {
      type: String,
      default: "api",
    },
    // "record"/"customer" — vezi @/Utile/ncrmActivityTimelineScope.mjs.
    scope: { type: String, default: "record" },
  },
  emits: ["loaded", "created", "changed", "error", "update:composerOpen", "update:composer-open"],
  data() {
    return {
      loading: false,
      submitting: false,
      downloadingRecordingId: null,
      activities: [],
      internalComposerOpen: false,
      editingActivityId: null,
      actionLoadingId: null,
      pendingCloseMap: {},
      form: buildDefaultNcrmActivityTimelineForm(),
      composerAllowedTypes: [],
      composerTypeLocked: false,
      composerNotice: { message: "", color: "info" },
      deleteDialog: false,
      deleteConfirmText: "",
      deleteLoading: false,
      deleteTarget: null,
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    normalizedEntityType() {
      return normalizeNcrmActivityEntityType(this.entityType);
    },
    normalizedEntityId() {
      return parseNcrmActivityEntityId(this.entityId);
    },
    normalizedOriginEntityType() {
      return normalizeNcrmActivityOriginEntityType(this.originEntityType);
    },
    normalizedOriginEntityId() {
      return parseNcrmActivityEntityId(this.originEntityId);
    },
    normalizedEntityContext() {
      return normalizeEntityContext(this.entityContext);
    },
    normalizedOnboardingId() {
      return parseNcrmActivityEntityId(this.onboardingId);
    },
    ...ncrmActivityTimelineScopeComputed,
    isClientPrioritySortMode() {
      return String(this.sortMode || "").trim().toLowerCase() === "active_overdue_first";
    },
    canLoad() {
      return Boolean(this.normalizedEntityType && this.normalizedEntityId);
    },
    activityTypeItems() {
      const allowedTypeSet = new Set(
        (this.composerAllowedTypes || [])
          .map((value) => normalizeNcrmActivityType(value))
          .filter(Boolean)
      );
      const sourceTypes = getNcrmActivityTypeOptions();
      const resolvedTypes = allowedTypeSet.size
        ? sourceTypes.filter((type) => allowedTypeSet.has(type))
        : sourceTypes;
      return resolvedTypes.map((type) => ({
        value: type,
        label: this.activityTypeLabel(type),
      }));
    },
    activityTypeFieldDisabled() {
      return this.submitting || this.composerTypeLocked || this.activityTypeItems.length <= 1;
    },
    activityStatusItems() {
      const type = this.form.activity_type;
      return getNcrmActivityStatusOptions(type).map((status) => ({
        value: status,
        label: this.activityStatusLabel(status, type),
      }));
    },
    activityPriorityItems() {
      return getNcrmActivityPriorityOptions().map((priority) => ({
        value: priority,
        label: this.activityPriorityLabel(priority),
      }));
    },
    selectMenuProps() {
      return {
        contentClass: "ncrm-activity-select-menu",
        zIndex: 2710,
      };
    },
    titleText() {
      return this.title || this.$t("ncrm.activity.title");
    },
    subtitleText() {
      return this.subtitle || this.$t("ncrm.activity.subtitle");
    },
    emptyValueLabel() {
      return this.$t("ncrm.contacts.details.emptyValue");
    },
    requiresDueAt() {
      return this.requiresDueForType(this.form.activity_type);
    },
    showStatusField() {
      return !this.isNoteType(this.form.activity_type);
    },
    showPriorityField() {
      return !this.isNoteType(this.form.activity_type);
    },
    submitDisabled() {
      if (!this.canLoad || this.submitting) return true;
      if (!String(this.form.content || "").trim()) return true;
      if (this.requiresDueAt && !normalizeNcrmActivityDateTimeInput(this.form.due_at)) return true;
      return false;
    },
    entityKey() {
      return `${this.normalizedEntityType || ""}:${this.normalizedEntityId || ""}:${this.normalizedEntityContext || ""}:${this.normalizedOnboardingId || ""}`;
    },
    resolvedComposerOpen() {
      return Boolean(this.internalComposerOpen);
    },
    composerTitle() {
      return this.isEditing
        ? this.$t("ncrm.activity.editTitle")
        : this.$t("ncrm.activity.addTitle");
    },
    isEditing() {
      return Number.isFinite(Number(this.editingActivityId)) && Number(this.editingActivityId) > 0;
    },
    deleteSubmitDisabled() {
      if (this.deleteLoading || !this.deleteTarget) return true;
      return String(this.deleteConfirmText || "").trim().toUpperCase() !== DELETE_CONFIRM_TOKEN;
    },
    displayActivities() {
      const source = Array.isArray(this.activities) ? this.activities : [];
      if (this.isClientPrioritySortMode) {
        const sorted = sortNcrmActivityTimelineItems(source);
        return this.maxItems > 0 ? sorted.slice(0, this.maxItems) : sorted;
      }
      return this.maxItems > 0 ? source.slice(0, this.maxItems) : source;
    },
  },
  watch: {
    entityKey: {
      immediate: true,
      handler() {
        this.clearAllPendingCloses();
        if (!this.autoLoad) return;
        if (!this.canLoad) {
          this.activities = [];
          return;
        }
        this.fetchActivities();
      },
    },
    composerOpen: {
      immediate: true,
      handler(nextValue) {
        this.internalComposerOpen = Boolean(nextValue);
        if (!this.internalComposerOpen && !this.submitting) {
          this.resetForm();
        }
      },
    },
    resolvedComposerOpen(nextValue) {
      if (!nextValue && !this.submitting) {
        this.resetForm();
      }
    },
    "form.activity_type": {
      handler(nextType) {
        const normalizedType = String(nextType || "").trim().toLowerCase() || "note";
        if (this.isNoteType(normalizedType)) {
          this.form.status = resolveNcrmActivityDefaultStatus("note");
          this.form.priority = "normal";
        } else {
          const normalizedStatus = normalizeNcrmActivityStatus(this.form.status, normalizedType, false);
          if (!normalizedStatus) {
            this.form.status = resolveNcrmActivityDefaultStatus(normalizedType);
          }
        }
        if (!this.requiresDueForType(normalizedType)) {
          this.form.due_at = "";
        }
      },
    },
  },
  created() {
    this.pendingCloseTimers = {};
  },
  beforeUnmount() {
    this.clearAllPendingCloses();
  },
  methods: {
    resetComposerTypeRestrictions() {
      this.composerAllowedTypes = [];
      this.composerTypeLocked = false;
    },
    resetComposerNotice() {
      this.composerNotice = { message: "", color: "info" };
    },
    updateComposerOpen(value) {
      const normalized = Boolean(value);
      this.internalComposerOpen = normalized;
      this.$emit("update:composerOpen", normalized);
      this.$emit("update:composer-open", normalized);
    },
    applyComposerPreset(preset = {}) {
      const allowedActivityTypesSource = Array.isArray(preset.allowed_activity_types)
        ? preset.allowed_activity_types
        : Array.isArray(preset.allowedActivityTypes)
          ? preset.allowedActivityTypes
          : [];
      const allowedActivityTypes = [...new Set(
        allowedActivityTypesSource
          .map((value) => normalizeNcrmActivityType(value))
          .filter(Boolean)
      )];
      this.composerAllowedTypes = allowedActivityTypes;
      this.composerTypeLocked = Boolean(preset.lock_activity_type ?? preset.lockActivityType)
        || allowedActivityTypes.length === 1;
      const noticeMessage = String(
        preset.notice_message ?? preset.noticeMessage ?? preset.helper_text ?? preset.helperText ?? ""
      ).trim();
      this.composerNotice = { message: noticeMessage, color: String(preset.notice_color ?? preset.noticeColor ?? "info").trim() || "info" };

      const presetType = normalizeNcrmActivityType(preset.activity_type ?? preset.activityType)
        || this.form.activity_type;
      const normalizedType = allowedActivityTypes.length && !allowedActivityTypes.includes(presetType)
        ? allowedActivityTypes[0]
        : presetType;

      this.form.activity_type = normalizedType;
      this.form.title = String(preset.title ?? "").trim();
      this.form.content = String(preset.content ?? "").trim();
      this.form.due_at = this.formatDateTimeLocal(preset.due_at ?? preset.dueAt);

      if (this.isNoteType(normalizedType)) {
        this.form.status = resolveNcrmActivityDefaultStatus("note");
        this.form.priority = "normal";
        this.form.due_at = "";
        return;
      }

      const normalizedStatus = normalizeNcrmActivityStatus(preset.status, normalizedType, false);
      this.form.status = normalizedStatus || resolveNcrmActivityDefaultStatus(normalizedType);
      this.form.priority = normalizeNcrmActivityPriority(preset.priority, "normal") || "normal";
    },
    openComposerWithPreset(preset = {}) {
      if (!this.canLoad) return;
      this.resetForm();
      this.applyComposerPreset(preset);
      this.updateComposerOpen(true);
    },
    openComposer() {
      this.openComposerWithPreset();
    },
    ...ncrmActivityTimelineActionMethods,
    ...ncrmActivityTimelineRecordingMethods,
    openComposerForEdit(activity) {
      const normalized = normalizeNcrmActivityItem(activity || {});
      if (!normalized?.id) return;
      if (!this.canManageActivity(normalized)) return;
      this.resetComposerTypeRestrictions();
      this.hydrateForm(normalized);
      this.editingActivityId = normalized.id;
      this.updateComposerOpen(true);
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        message: String(message || ""),
        color,
      };
    },
    formatDateTimeLocal(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return "";
      const pad = (number) => String(number).padStart(2, "0");
      return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    activityTypeLabel(type) {
      const normalized = String(type || "").trim().toLowerCase();
      const key = `ncrm.activity.types.${normalized || "note"}`;
      const fallback = this.$t("ncrm.activity.types.note");
      const value = this.$t(key);
      return value === key ? fallback : value;
    },
    activityStatusLabel(status, type) {
      const normalizedType = String(type || "").trim().toLowerCase() || "note";
      const normalizedStatus = normalizeNcrmActivityStatus(status, normalizedType, true);
      const key = `ncrm.activity.statuses.${normalizedStatus || "logged"}`;
      const fallback = normalizedStatus || this.$t("ncrm.activity.statuses.logged");
      const value = this.$t(key);
      return value === key ? fallback : value;
    },
    activityPriorityLabel(priority) {
      const normalized = normalizeNcrmActivityPriority(priority, "normal") || "normal";
      const key = `ncrm.activity.priorities.${normalized}`;
      const fallback = normalized;
      const value = this.$t(key);
      return value === key ? fallback : value;
    },
    activityIcon(type) {
      const normalized = String(type || "").trim().toLowerCase();
      if (normalized === "todo") return "mdi-checkbox-marked-outline";
      if (normalized === "call") return "mdi-phone";
      if (normalized === "email") return "mdi-email-outline";
      if (normalized === "meeting") return "mdi-calendar-check-outline";
      return "mdi-note-text-outline";
    },
    activityStatusColor(activity) {
      const normalizedStatus = String(activity?.status || "").trim().toLowerCase();
      if (["open", "planned", "in_progress"].includes(normalizedStatus)) return "warning";
      if (["done", "held", "sent", "replied", "logged"].includes(normalizedStatus)) return "success";
      if (["canceled", "failed", "no_answer", "no_show"].includes(normalizedStatus)) return "error";
      return "primary";
    },
    activityPriorityColor(priority) {
      const normalized = String(priority || "").trim().toLowerCase();
      if (normalized === "high") return "error";
      if (normalized === "low") return "default";
      return "primary";
    },
    activityDisplayDate(activity) {
      const value = activity?.due_at || activity?.happened_at || activity?.created_at;
      if (!value) return this.emptyValueLabel;
      const parsed = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return String(value);
      const dd = String(parsed.getDate()).padStart(2, "0");
      const mo = String(parsed.getMonth() + 1).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      const HH = String(parsed.getHours()).padStart(2, "0");
      const min = String(parsed.getMinutes()).padStart(2, "0");
      return `${dd}.${mo}.${yyyy}, ${HH}:${min}`;
    },
    requiresDueForType(type) {
      return isNcrmActivityProgrammableType(type);
    },
    isNoteType(type) {
      return String(type || "").trim().toLowerCase() === "note";
    },
    canCloseActivity(activity) {
      if (!this.canManageActivity(activity)) return false;
      const type = String(activity?.activity_type || "").trim().toLowerCase();
      if (!this.requiresDueForType(type)) return false;
      return isNcrmActivityOpenStatus(type, activity?.status);
    },
    isClosedTaskActivity(activity) {
      const type = String(activity?.activity_type || "").trim().toLowerCase();
      if (!this.requiresDueForType(type)) return false;
      return !isNcrmActivityOpenStatus(type, activity?.status);
    },
    ...ncrmActivityTimelineScopeMethods,
    buildActivityView(activity) {
      const type = activity?.activity_type;
      const isNote = this.isNoteType(type);
      const statusColor = this.activityStatusColor(activity);
      const recordingCallId = this.resolveRecordingCallId(activity);
      const provenance = this.activityProvenanceLabel(activity);
      return {
        title: this.resolveActivityTitle(activity),
        content: this.activityDisplayContent(activity),
        dateText: this.activityDisplayDate(activity),
        iconName: this.activityIcon(type),
        statusLabel: isNote ? "" : this.activityStatusLabel(activity?.status, type),
        statusColor,
        priorityLabel: isNote ? "" : this.activityPriorityLabel(activity?.priority),
        priorityColor: this.activityPriorityColor(activity?.priority),
        originContext: provenance || this.activityOriginContext(activity),
        originClickable: !provenance && Boolean(this.activityOriginRoute(activity)),
        accentColorKey: isNote ? "primary" : statusColor,
        isNote,
        isClosed: this.isClosedTaskActivity(activity),
        canClose: this.canCloseActivity(activity),
        canManage: this.canManageActivity(activity),
        recordingAvailable: Boolean(recordingCallId),
      };
    },
    resetForm() {
      this.editingActivityId = null;
      this.resetComposerTypeRestrictions();
      this.resetComposerNotice();
      this.form = buildDefaultNcrmActivityTimelineForm();
    },
    hydrateForm(activity) {
      const normalized = normalizeNcrmActivityItem(activity || {});
      this.form = hydrateNcrmActivityTimelineForm({
        activity: normalized,
        isNoteType: this.isNoteType,
        formatDateTimeLocal: this.formatDateTimeLocal,
      });
    },
    buildPayloadFromForm() {
      return buildNcrmActivityTimelineFormPayload({
        entityType: this.normalizedEntityType,
        entityId: this.normalizedEntityId,
        originEntityType: this.normalizedOriginEntityType,
        originEntityId: this.normalizedOriginEntityId,
        form: this.form,
        isNoteType: this.isNoteType,
        requiresDueForType: this.requiresDueForType,
      });
    },
    async fetchActivitiesForPrioritySort() {
      const items = [];
      let total = null;

      for (let page = 1; page <= PRIORITY_SORT_MAX_PAGES; page += 1) {
        const { data } = await axios.get("/ncrm/activity", {
          params: this.buildFetchParams({
            page,
            limitOverride: PRIORITY_SORT_FETCH_LIMIT,
          }),
        });
        const list = normalizeNcrmActivityList(data);
        if (list.length) {
          items.push(...list);
        }

        const responseTotal = Number(data?.total);
        if (Number.isFinite(responseTotal) && responseTotal >= 0) {
          total = Math.trunc(responseTotal);
        }

        if (!list.length) break;
        if (total !== null && items.length >= total) break;
        if (list.length < PRIORITY_SORT_FETCH_LIMIT) break;
      }

      return items;
    },
    async fetchActivities() {
      if (!this.canLoad) return;
      this.loading = true;
      try {
        let list = [];
        if (this.isClientPrioritySortMode) {
          list = await this.fetchActivitiesForPrioritySort();
        } else {
          const { data } = await axios.get("/ncrm/activity", {
            params: this.buildFetchParams(),
          });
          list = normalizeNcrmActivityList(data);
        }
        this.activities = Array.isArray(list) ? list : [];
        this.$emit("loaded", this.displayActivities.slice());
      } catch (error) {
        console.error("[ncrm] load activity failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.activity.messages.loadError"),
          "error"
        );
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    async saveComposer() {
      const wasEditing = this.isEditing;
      const { basePayload, payload } = this.buildPayloadFromForm();

      if (!basePayload.entity_type || !basePayload.entity_id) {
        this.showSnackbar(this.$t("ncrm.activity.validation.entityRequired"), "error");
        return;
      }
      if (!payload.content) {
        this.showSnackbar(this.$t("ncrm.activity.validation.contentRequired"), "error");
        return;
      }
      if (this.requiresDueAt && !payload.due_at) {
        this.showSnackbar(this.$t("ncrm.activity.validation.dueRequired"), "error");
        return;
      }

      this.submitting = true;
      try {
        let saved;
        if (wasEditing) {
          const { data } = await axios.patch(`/ncrm/activity/${this.editingActivityId}`, payload);
          saved = normalizeNcrmActivityItem(data || payload);
        } else {
          const createPayload = {
            ...payload,
            entity_type: basePayload.entity_type,
            entity_id: basePayload.entity_id,
          };
          if (this.normalizedEntityContext === "onboarding" && this.normalizedOnboardingId) {
            createPayload.onboarding_id = this.normalizedOnboardingId;
          }
          if (basePayload.origin_entity_type && basePayload.origin_entity_id) {
            createPayload.origin_entity_type = basePayload.origin_entity_type;
            createPayload.origin_entity_id = basePayload.origin_entity_id;
          }
          const { data } = await axios.post("/ncrm/activity", createPayload);
          saved = normalizeNcrmActivityItem(data || createPayload);
        }

        if (this.autoLoad) {
          await this.fetchActivities();
        } else if (wasEditing) {
          this.activities = this.activities.map((item) => (item.id === saved.id ? saved : item));
        } else {
          this.activities = [saved, ...this.activities];
        }

        this.resetForm();
        this.updateComposerOpen(false);
        this.showSnackbar(
          wasEditing
            ? this.$t("ncrm.activity.messages.updated")
            : this.$t("ncrm.activity.messages.created"),
          "success"
        );
        this.$emit("created", saved);
        this.$emit("changed", {
          action: wasEditing ? "updated" : "created",
          activity: saved,
        });
      } catch (error) {
        console.error("[ncrm] save activity failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.activity.messages.saveError"),
          "error"
        );
        this.$emit("error", error);
      } finally {
        this.submitting = false;
      }
    },
    requestCloseActivity(activity) {
      const normalized = normalizeNcrmActivityItem(activity || {});
      if (!normalized?.id) return;
      if (!this.canCloseActivity(normalized)) return;

      const id = normalized.id;
      if (this.pendingCloseMap[id]) return;

      this.pendingCloseMap = {
        ...this.pendingCloseMap,
        [id]: { remaining: PENDING_CLOSE_SECONDS, activity: normalized },
      };

      const interval = window.setInterval(() => {
        const entry = this.pendingCloseMap[id];
        if (!entry) {
          this.clearPendingTimer(id);
          return;
        }
        const remaining = entry.remaining - 1;
        if (remaining <= 0) {
          this.finalizePendingClose(id);
        } else {
          this.pendingCloseMap = {
            ...this.pendingCloseMap,
            [id]: { ...entry, remaining },
          };
        }
      }, 1000);

      this.pendingCloseTimers[id] = interval;
    },
    clearPendingTimer(id) {
      const timer = this.pendingCloseTimers[id];
      if (timer) {
        window.clearInterval(timer);
        delete this.pendingCloseTimers[id];
      }
    },
    removePendingCloseEntry(id) {
      if (!(id in this.pendingCloseMap)) return null;
      const next = { ...this.pendingCloseMap };
      const entry = next[id];
      delete next[id];
      this.pendingCloseMap = next;
      return entry;
    },
    undoPendingClose(id) {
      this.clearPendingTimer(id);
      this.removePendingCloseEntry(id);
    },
    clearAllPendingCloses() {
      Object.keys(this.pendingCloseTimers || {}).forEach((id) => {
        window.clearInterval(this.pendingCloseTimers[id]);
      });
      this.pendingCloseTimers = {};
      if (Object.keys(this.pendingCloseMap).length) {
        this.pendingCloseMap = {};
      }
    },
    finalizePendingClose(id) {
      this.clearPendingTimer(id);
      const entry = this.removePendingCloseEntry(id);
      if (!entry?.activity) return;
      this.closeActivity(entry.activity);
    },
    async closeActivity(activity) {
      const normalized = normalizeNcrmActivityItem(activity || {});
      if (!normalized?.id) return;
      if (!this.canCloseActivity(normalized)) return;

      this.actionLoadingId = normalized.id;
      try {
        await axios.patch(`/ncrm/activity/${normalized.id}`, {
          status: resolveNcrmActivityDefaultClosedStatus(normalized.activity_type),
        });
        await this.fetchActivities();
        this.showSnackbar(this.$t("ncrm.activity.messages.closed"), "success");
        this.$emit("changed", {
          action: "closed",
          activity: normalized,
        });
      } catch (error) {
        console.error("[ncrm] close activity failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.activity.messages.closeError"),
          "error"
        );
        this.$emit("error", error);
      } finally {
        this.actionLoadingId = null;
      }
    },
    openDeleteDialog(activity) {
      const normalized = normalizeNcrmActivityItem(activity || {});
      if (!normalized?.id) return;
      if (!this.canManageActivity(normalized)) return;
      this.undoPendingClose(normalized.id);
      this.deleteTarget = normalized;
      this.deleteConfirmText = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog(force = false) {
      if (this.deleteLoading && !force) return;
      this.deleteDialog = false;
      this.deleteConfirmText = "";
      this.deleteTarget = null;
    },
    async confirmDelete() {
      const activityId = parseNcrmActivityEntityId(this.deleteTarget?.id);
      if (!activityId) return;

      this.deleteLoading = true;
      try {
        await axios.delete(`/ncrm/activity/${activityId}`, {
          data: {
            confirmText: DELETE_CONFIRM_TOKEN,
          },
        });
        await this.fetchActivities();
        this.closeDeleteDialog(true);
        this.showSnackbar(this.$t("ncrm.activity.messages.deleted"), "success");
        this.$emit("changed", {
          action: "deleted",
          activity: {
            id: activityId,
            entity_type: this.normalizedEntityType,
            entity_id: this.normalizedEntityId,
          },
        });
      } catch (error) {
        console.error("[ncrm] delete activity failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.activity.messages.deleteError"),
          "error"
        );
        this.$emit("error", error);
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-activity-timeline__title {
  color: var(--ncrm-color-text-strong);
}

.ncrm-activity-timeline__rail {
  position: relative;
  padding-left: 28px;
}

.ncrm-activity-timeline__rail-line {
  position: absolute;
  left: 10px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  border-radius: var(--app-radius-pill);
  background: var(--ncrm-color-border-default);
}
</style>
